import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import WCSubmenu from '@/components/worldcup/WCSubmenu';
import { LoadingPage } from '@/components/common/Loading';
import { MapPin, Calendar, Building2, ChevronRight, Users, Star, Clock, Ruler } from 'lucide-react';
import { translateSurface, translateCountryName, wcMatchSlug } from '@/lib/wcTranslations';

const API_BASE = '';
const STADIUM_HERO = 'https://static.prod-images.emergentagent.com/jobs/2b79e76a-42eb-4df3-ab27-72c6bfd04714/images/e9979bf9f1535ee42b01c32e789dfde52741353f493f4572e0b23ec8b78a25c7.png';

export default function WCStadiumPage() {
  const { slug } = useParams();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const wcBase = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;
  const isTR = currentLang === 'tr';

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`${API_BASE}/api/worldcup/stadium/${slug}?lang=${currentLang}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [slug, currentLang]);

  if (loading) return <LoadingPage />;
  if (!data?.stadium) return <div className="container mx-auto px-4 py-8 text-slate-400">Stadyum bulunamadı</div>;

  const { stadium: s, fixtures } = data;
  const matchPath = isTR ? 'mac' : 'match';
  const countryNames = { USA: isTR ? 'ABD' : 'USA', Canada: isTR ? 'Kanada' : 'Canada', Mexico: isTR ? 'Meksika' : 'Mexico' };

  return (
    <div data-testid="wc-stadium-page">
      <WCSubmenu />
      <SEOHead title={`${s.name} - ${isTR ? 'Dünya Kupası 2026' : 'World Cup 2026'} | ODDSIS`}
        description={isTR ? s.description_tr : s.description_en}
        canonical={data.seo?.canonical} jsonLd={data.seo?.jsonld} />

      {/* Hero */}
      <div className="relative overflow-hidden h-64 md:h-80">
        <img src={s.hero_image || s.image || STADIUM_HERO} alt={s.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 z-10 container mx-auto px-4 max-w-6xl pb-8">
          <div className="flex items-center gap-2 text-sm text-slate-300 mb-3">
            <Link to={`/${currentLang}`} className="hover:text-white">{isTR ? 'Ana Sayfa' : 'Home'}</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={wcBase} className="hover:text-amber-300 text-amber-400/70">WC 2026</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`${wcBase}/${isTR ? "ev-sahibi" : "host"}/${s.country_slug === "usa" && isTR ? "abd" : s.country_slug === "canada" && isTR ? "kanada" : s.country_slug === "mexico" && isTR ? "meksika" : s.country_slug}`} className="hover:text-amber-300 text-amber-400/70">{countryNames[s.country]}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{s.name}</span>
          </div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl md:text-4xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.name}</h1>
            {s.hosts_final && (
              <span className="flex items-center gap-1 px-3 py-1 bg-amber-500/20 border border-amber-500/30 rounded-full text-xs font-bold text-amber-400 shadow-[0_0_15px_rgba(245,158,11,0.2)]">
                <Star className="w-3 h-3 fill-amber-400" />FİNAL
              </span>
            )}
          </div>
          <p className="text-sm text-slate-300 mt-1 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" />{s.display_city || s.city}, {s.state}, {countryNames[s.country]}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-6xl py-8 space-y-8">
        {/* Info Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: Users, val: (s.capacity || 0).toLocaleString(), label: isTR ? 'Kapasite' : 'Capacity' },
            { icon: Calendar, val: s.opened, label: isTR ? 'Açılış Yılı' : 'Opened' },
            { icon: Star, val: s.wc_matches, label: isTR ? 'WC Maç' : 'WC Matches' },
            { icon: Ruler, val: translateSurface(s.surface, currentLang) || 'N/A', label: isTR ? 'Zemin' : 'Surface' },
          ].map(c => (
            <div key={c.label} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
              <c.icon className="w-5 h-5 mx-auto text-amber-400/60 mb-2" />
              <p className="text-xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.val}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        {/* Description */}
        <section className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
          <h2 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Building2 className="w-5 h-5 inline mr-2 text-amber-400" />{isTR ? 'Stadyum Hakkında' : 'About Stadium'}
          </h2>
          <p className="text-slate-300 leading-relaxed">{isTR ? s.description_tr : s.description_en}</p>
        </section>

        {/* Facts */}
        {s.facts?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {s.facts.map((f, i) => (
              <span key={i} className="px-3 py-1.5 rounded-full bg-amber-500/5 border border-amber-500/20 text-xs font-medium text-amber-400/80">
                <Star className="w-3 h-3 inline mr-1" />{f}
              </span>
            ))}
          </div>
        )}

        {/* Matches at this stadium */}
        {fixtures?.length > 0 && (
          <section>
            <h2 className="text-lg font-bold text-white uppercase tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Calendar className="w-5 h-5 inline mr-2 text-amber-400" />{isTR ? 'Bu Stadyumdaki Maçlar' : 'Matches at This Stadium'} ({fixtures.length})
            </h2>
            <div className="space-y-1.5">
              {fixtures.map(m => {
                const date = m.date ? new Date(m.date) : null;
                const isTurkiye = m.home_name === 'Türkiye' || m.away_name === 'Türkiye';
                return (
                  <Link key={m.id} to={`${wcBase}/${matchPath}/${wcMatchSlug(m.home_name, m.away_name, m.id, currentLang)}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all hover:bg-slate-800/50 hover:border-amber-500/20 ${
                      isTurkiye ? 'border-red-500/20 bg-red-500/5' : 'border-slate-800/50 bg-slate-900/30'
                    }`}>
                    <span className="text-xs text-slate-500 w-20 shrink-0">
                      {date?.toLocaleDateString(isTR ? 'tr-TR' : 'en-US', { day: 'numeric', month: 'short' })}
                    </span>
                    <span className="text-xs text-slate-500 w-12 shrink-0">
                      {date?.toLocaleTimeString(isTR ? 'tr-TR' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <div className="flex items-center gap-2 flex-1 justify-end">
                      <span className={`text-sm font-semibold text-right ${m.home_name === 'Türkiye' ? 'text-red-400' : 'text-slate-200'}`}>{translateCountryName(m.home_name, currentLang)}</span>
                      <img src={m.home_logo} alt="" className="w-5 h-5" />
                    </div>
                    <span className="text-xs text-slate-600 px-2">vs</span>
                    <div className="flex items-center gap-2 flex-1">
                      <img src={m.away_logo} alt="" className="w-5 h-5" />
                      <span className={`text-sm font-semibold ${m.away_name === 'Türkiye' ? 'text-red-400' : 'text-slate-200'}`}>{translateCountryName(m.away_name, currentLang)}</span>
                    </div>
                    <span className="text-[10px] text-amber-400/60">{isTR ? m.round.replace('Group Stage - ','Grup - ') : m.round}</span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Internal Links */}
        <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-800">
          <Link to={`${wcBase}/${isTR ? "ev-sahibi" : "host"}/${s.country_slug === "usa" && isTR ? "abd" : s.country_slug === "canada" && isTR ? "kanada" : s.country_slug === "mexico" && isTR ? "meksika" : s.country_slug}`} className="text-xs text-amber-400/70 hover:text-amber-300 bg-slate-800/50 px-3 py-1.5 rounded-lg">
            {countryNames[s.country]} {isTR ? 'Sayfası' : 'Page'}
          </Link>
          <Link to={`${wcBase}/${isTR ? "ev-sahibi" : "host"}/${s.country_slug === "usa" && isTR ? "abd" : s.country_slug === "canada" && isTR ? "kanada" : s.country_slug === "mexico" && isTR ? "meksika" : s.country_slug}/${s.city_slug}`} className="text-xs text-slate-400 hover:text-amber-300 bg-slate-800/50 px-3 py-1.5 rounded-lg">
            {s.display_city || s.city}
          </Link>
          <Link to={`${wcBase}/${isTR ? 'stadyumlar' : 'stadiums'}`} className="text-xs text-slate-400 hover:text-amber-300 bg-slate-800/50 px-3 py-1.5 rounded-lg">
            {isTR ? 'Tüm Stadyumlar' : 'All Stadiums'}
          </Link>
          <Link to={`${wcBase}/${isTR ? 'skor-ve-fikstur' : 'scores'}`} className="text-xs text-slate-400 hover:text-amber-300 bg-slate-800/50 px-3 py-1.5 rounded-lg">
            {isTR ? 'Tüm Fikstür' : 'All Fixtures'}
          </Link>
        </div>
      </div>
    </div>
  );
}
